import { Stack, Typography, Box } from "@mui/material";
import Component from "components/Component";
import Disabled from "components/common/Disabled";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import * as programPricingResolver from "domain/programPricingResolver";
import { useDispatch, useSelector } from "react-redux";
import * as staticDataService from "services/staticDataService";
import * as SOVStore from "store/SOV";
import * as analyticsStore from "store/analytics";
import * as programPricingStore from "store/programPricing";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";
import * as stateSelectors from "store/selectors/stateSelectors";

const formatNumber = (value) => {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const MathSymbol = ({ symbol }) => (
  <Box sx={{ display: "flex", alignItems: "center", height: "56px", pt: 1 }}>
    <Typography variant={"strong"}>{symbol}</Typography>
  </Box>
);

const ConfiguredProgramTiv = ({ fields }) => {
  const dispatch = useDispatch();
  const programPricing = useSelector(programPricingStore.selectInput);
  const calculatedValues = useSelector(analyticsStore.select)?.programPricing
    ?.values;

  const handleChange = (key, value) => {
    const otherSum = fields
      .filter((f) => f.key !== key)
      .reduce((sum, f) => sum + Number(programPricing?.[f.key] ?? 0), 0);
    dispatch(
      programPricingStore.update({
        [key]: value,
        TIV: otherSum + Number(value ?? 0),
      })
    );
  };

  const tiv = fields.reduce(
    (sum, f) => sum + Number(programPricing?.[f.key] ?? 0),
    0
  );

  return (
    <Component title={"TIV"} lg_width={6}>
      <Stack
        direction={"row"}
        spacing={2}
        alignItems={"flex-start"}
        sx={{ minHeight: "80px" }}
      >
        {fields.map((field, i) => {
          const actualValue = field.sovSummaryKey
            ? calculatedValues?.meta?.[field.sovSummaryKey]
            : null;
          const helperText =
            actualValue != null ? formatNumber(actualValue) : "";
          return (
            <>
              <Box key={field.key} sx={{ flex: 1 }}>
                <Disabled ifReadOnly>
                  <PrettyNumberTextField
                    InputLabelProps={{ shrink: true }}
                    label={field.label}
                    value={programPricing?.[field.key] ?? ""}
                    onChangeNumber={(v) => handleChange(field.key, v)}
                    variant={"filled"}
                    helperText={helperText}
                    fullWidth
                  />
                </Disabled>
              </Box>
              {i < fields.length - 1 && <MathSymbol symbol={"+"} />}
            </>
          );
        })}
        <MathSymbol symbol={"="} />
        <Box sx={{ flex: 1 }}>
          <PrettyNumberTextField
            InputLabelProps={{ shrink: true }}
            label={"TIV"}
            value={tiv || ""}
            variant={"filled"}
            helperText={
              calculatedValues?.meta?.sum_tiv
                ? formatNumber(calculatedValues.meta.sum_tiv)
                : ""
            }
            disabled
            fullWidth
          />
        </Box>
      </Stack>
    </Component>
  );
};

const LegacyProgramTiv = () => {
  const dispatch = useDispatch();
  const calculatedValues = useSelector(analyticsStore.select)?.programPricing
    ?.values;

  const priorState = useSelector(stateSelectors.selectPriorState) || {};

  const sov = useSelector(SOVStore.selectSOV);
  const nonPdFieldType = sov.nonPdFieldType;
  const nonPdFieldKey = nonPdFieldType.toLowerCase();

  const resolvedProgramPricing = useSelector(programPricingStore.select) || {};

  const priorCalculatedValues = priorState?.analytics?.programPricing?.values;
  const priorValueTIV = priorCalculatedValues?.meta?.sum_tiv;
  const priorValueNonPD = priorCalculatedValues?.meta?.[`sum_${nonPdFieldKey}`];

  const priorResolvedLayers = pricingSelectors.selectResolvedLayers(priorState);
  const priorResolvedProgramPricing = programPricingResolver.resolve(
    priorState?.programPricing,
    {
      layers: priorResolvedLayers,
      SOV: priorState?.SOV,
    }
  );

  const priorInputPD = priorResolvedProgramPricing?.PD;
  const priorInputNonPD = priorResolvedProgramPricing?.[nonPdFieldType];
  const priorInputTIV = priorResolvedProgramPricing?.TIV;

  const updateProgramPricing = (values) => {
    dispatch(programPricingStore.update(values));
  };

  const autoValueTIV = calculatedValues?.meta?.sum_tiv || 0;
  const autoValueNonPD = calculatedValues?.meta?.[`sum_${nonPdFieldKey}`] || 0;
  const autoTIV = formatNumber(autoValueTIV);
  const autoNonPD = formatNumber(autoValueNonPD);

  const priorTIV =
    typeof priorValueTIV === "number" ? formatNumber(priorValueTIV) : null;
  const priorNonPD =
    typeof priorValueNonPD === "number" ? formatNumber(priorValueNonPD) : null;

  const nonPdHelperText = autoNonPD || "";
  const tivHelperText = autoTIV || "";

  const hasPriorData = priorNonPD || priorTIV;

  const onChangeNumber = (key) => (value) =>
    updateProgramPricing({ [key]: value });

  return (
    <Component title={"TIV"} lg_width={6}>
      <Stack spacing={2}>
        <Stack
          direction={"row"}
          spacing={2}
          alignItems={"flex-start"}
          sx={{ minHeight: "80px" }}
        >
          <Box sx={{ flex: 1 }}>
            <Disabled ifReadOnly>
              <PrettyNumberTextField
                InputLabelProps={{ shrink: true }}
                label={"PD"}
                value={resolvedProgramPricing.PD || ""}
                onChangeNumber={onChangeNumber("PD")}
                variant={"filled"}
                helperText={"Actual: "}
                disabled={resolvedProgramPricing.calculated === "PD"}
                fullWidth
              />
            </Disabled>
          </Box>
          <MathSymbol symbol={"+"} />
          <Box sx={{ flex: 1 }}>
            <Disabled ifReadOnly>
              <PrettyNumberTextField
                InputLabelProps={{ shrink: true }}
                label={nonPdFieldType}
                value={resolvedProgramPricing[nonPdFieldType] || ""}
                onChangeNumber={onChangeNumber(nonPdFieldType)}
                variant={"filled"}
                helperText={nonPdHelperText}
                disabled={resolvedProgramPricing.calculated === nonPdFieldType}
                fullWidth
              />
            </Disabled>
          </Box>
          <MathSymbol symbol={"="} />
          <Box sx={{ flex: 1 }}>
            <Disabled ifReadOnly>
              <PrettyNumberTextField
                InputLabelProps={{ shrink: true }}
                label={"TIV"}
                value={resolvedProgramPricing.TIV || ""}
                onChangeNumber={onChangeNumber("TIV")}
                variant={"filled"}
                helperText={tivHelperText}
                disabled={resolvedProgramPricing.calculated === "TIV"}
                fullWidth
              />
            </Disabled>
          </Box>
        </Stack>

        {hasPriorData && (
          <Stack
            direction={"row"}
            spacing={2}
            alignItems={"flex-start"}
            sx={{ opacity: 0.7 }}
          >
            <Box sx={{ flex: 1 }}>
              <PrettyNumberTextField
                InputLabelProps={{ shrink: true }}
                label={"PD (Prior)"}
                value={priorInputPD || ""}
                variant={"filled"}
                disabled
                helperText={priorInputPD ? "Prior Calculated" : "Not available"}
                fullWidth
              />
            </Box>
            <MathSymbol symbol={"+"} />
            <Box sx={{ flex: 1 }}>
              <PrettyNumberTextField
                InputLabelProps={{ shrink: true }}
                label={`${nonPdFieldType} (Prior)`}
                value={priorInputNonPD || priorNonPD || ""}
                variant={"filled"}
                disabled
                helperText={priorNonPD || ""}
                fullWidth
              />
            </Box>
            <MathSymbol symbol={"="} />
            <Box sx={{ flex: 1 }}>
              <PrettyNumberTextField
                InputLabelProps={{ shrink: true }}
                label={"TIV (Prior)"}
                value={priorInputTIV || priorTIV || ""}
                variant={"filled"}
                disabled
                helperText={priorTIV || ""}
                fullWidth
              />
            </Box>
          </Stack>
        )}
      </Stack>
    </Component>
  );
};

const ProgramTiv = () => {
  const { data: tivConfig } = staticDataService.useConfigQuery("tiv_config");
  const fields = tivConfig?.fields;

  if (fields?.length) {
    return <ConfiguredProgramTiv fields={fields} />;
  }

  return <LegacyProgramTiv />;
};

export default ProgramTiv;
